import { useCallback, useEffect, useState } from 'react';

import { alterarSenha, buscarPerfil, salvarPerfil } from '@/api/conta';
import { useAuth } from '@/context/AuthContext';
import { useRequisicao } from '@/hooks/useRequisicao';

import {
  AlertaErro,
  AlertaSucesso,
  BotaoPrimario,
  CampoTexto,
} from '../auth/AuthShell';
import { Carregando, Cartao, ErroCarregamento, Secao } from './ContaUI';

const CAMPOS_ENDERECO = [
  { nome: 'addressCep', rotulo: 'CEP' },
  { nome: 'addressStreet', rotulo: 'Rua' },
  { nome: 'addressNumber', rotulo: 'Número' },
  { nome: 'addressComplement', rotulo: 'Complemento' },
  { nome: 'addressNeighborhood', rotulo: 'Bairro' },
  { nome: 'addressCity', rotulo: 'Cidade' },
  { nome: 'addressState', rotulo: 'Estado (UF)' },
];

export default function DadosESenha() {
  const buscar = useCallback(buscarPerfil, []);
  const { dados, carregando, erro, recarregar } = useRequisicao(buscar);
  const { refreshUser } = useAuth();

  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [msgPerfil, setMsgPerfil] = useState('');
  const [erroPerfil, setErroPerfil] = useState('');

  const [senhas, setSenhas] = useState({ atual: '', nova: '', confirma: '' });
  const [trocando, setTrocando] = useState(false);
  const [msgSenha, setMsgSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');

  useEffect(() => {
    if (dados) setForm(dados);
  }, [dados]);

  if (carregando || !form) {
    return (
      <Secao titulo="Dados e senha">
        <Carregando linhas={2} />
      </Secao>
    );
  }

  if (erro) {
    return (
      <Secao titulo="Dados e senha">
        <ErroCarregamento onTentarNovamente={recarregar} />
      </Secao>
    );
  }

  const atualizar = (campo) => (e) =>
    setForm((atual) => ({ ...atual, [campo]: e.target.value }));

  const enviarPerfil = async (e) => {
    e.preventDefault();
    setMsgPerfil('');
    setErroPerfil('');
    setSalvando(true);
    try {
      await salvarPerfil({
        name: form.name ?? '',
        phone: form.phone ?? '',
        addressCep: form.addressCep ?? '',
        addressStreet: form.addressStreet ?? '',
        addressNumber: form.addressNumber ?? '',
        addressComplement: form.addressComplement ?? '',
        addressNeighborhood: form.addressNeighborhood ?? '',
        addressCity: form.addressCity ?? '',
        addressState: form.addressState ?? '',
      });
      await refreshUser();
      setMsgPerfil('Dados atualizados.');
    } catch {
      setErroPerfil('Não foi possível salvar agora. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const enviarSenha = async (e) => {
    e.preventDefault();
    setMsgSenha('');
    setErroSenha('');

    if (senhas.nova.length < 6) {
      setErroSenha('A nova senha precisa ter pelo menos 6 caracteres.');
      return;
    }
    if (senhas.nova !== senhas.confirma) {
      setErroSenha('A confirmação não confere com a nova senha.');
      return;
    }

    setTrocando(true);
    try {
      await alterarSenha(senhas.atual, senhas.nova);
      setSenhas({ atual: '', nova: '', confirma: '' });
      setMsgSenha('Senha alterada.');
    } catch (err) {
      setErroSenha(
        err?.response?.data?.erro ||
          'Não foi possível alterar a senha agora. Tente novamente.',
      );
    } finally {
      setTrocando(false);
    }
  };

  return (
    <div className="space-y-10">
      <Secao titulo="Dados e senha" descricao="Suas informações de cadastro">
        <Cartao>
          <form onSubmit={enviarPerfil} className="space-y-5">
            <CampoTexto
              rotulo="Nome completo"
              value={form.name ?? ''}
              onChange={atualizar('name')}
            />

            <CampoTexto
              rotulo="E-mail"
              value={form.email ?? ''}
              disabled
              dica="O e-mail é o identificador da conta e não pode ser alterado aqui."
            />

            <CampoTexto
              rotulo="Telefone"
              type="tel"
              value={form.phone ?? ''}
              onChange={atualizar('phone')}
            />

            <div className="border-petal grid gap-5 border-t pt-5 sm:grid-cols-2">
              {CAMPOS_ENDERECO.map((campo) => (
                <CampoTexto
                  key={campo.nome}
                  rotulo={campo.rotulo}
                  value={form[campo.nome] ?? ''}
                  onChange={atualizar(campo.nome)}
                />
              ))}
            </div>

            <AlertaErro>{erroPerfil}</AlertaErro>
            <AlertaSucesso>{msgPerfil}</AlertaSucesso>

            <BotaoPrimario type="submit" carregando={salvando}>
              Salvar dados
            </BotaoPrimario>
          </form>
        </Cartao>
      </Secao>

      <Secao titulo="Trocar senha">
        <Cartao>
          <form onSubmit={enviarSenha} className="space-y-5">
            <CampoTexto
              rotulo="Senha atual"
              type="password"
              autoComplete="current-password"
              value={senhas.atual}
              onChange={(e) =>
                setSenhas((s) => ({ ...s, atual: e.target.value }))
              }
            />
            <CampoTexto
              rotulo="Nova senha"
              type="password"
              autoComplete="new-password"
              value={senhas.nova}
              onChange={(e) =>
                setSenhas((s) => ({ ...s, nova: e.target.value }))
              }
              dica="Mínimo de 6 caracteres."
            />
            <CampoTexto
              rotulo="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              value={senhas.confirma}
              onChange={(e) =>
                setSenhas((s) => ({ ...s, confirma: e.target.value }))
              }
            />

            <AlertaErro>{erroSenha}</AlertaErro>
            <AlertaSucesso>{msgSenha}</AlertaSucesso>

            <BotaoPrimario type="submit" carregando={trocando}>
              Alterar senha
            </BotaoPrimario>
          </form>
        </Cartao>
      </Secao>
    </div>
  );
}
